//* * TODO: Fix eslint errors */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { useState, useEffect, useReducer } from "react";
import { Dialog, DialogTitle } from "@mui/material";
import isEqual from "lodash/isEqual";
import { useNavigate } from "react-router";
import { useApi } from "@platform/api";

import ConfirmDialog from "../../components/confirmDialog";
import { formatDate, getClientPayload } from "../../utils";
import useToastNotificationService from "../../hooks/useToastNotificationService";
import { usePrepareClientFormState } from "../../hooks";
import {
  CLIENT_FORM,
  CLIENT_REDUCER_ACTIONS,
  IS_CLIENT_FORM_VALID,
  CUSTOM_CONF_FIELDS,
} from "../../constants/client";
import { ROUTE_NAMES } from "../../constants";
import reducer, { initState } from "../../reducers/client";

import ClientDialogContent from "./clientDialogContent";

const { CLEAN_CLIENT, UPDATE_CLIENT_FIELD } = CLIENT_REDUCER_ACTIONS;
const { CLIENT_SINCE, STATE, SITUS_STATE } = CLIENT_FORM;

interface Props {
  open: boolean;
  onClose: VoidFunction;
  brokerId?: number;
}

function CreateClientDialog({
  open,
  onClose,
  brokerId,
}: Props): React.JSX.Element {
  const api = useApi();
  const navigation = useNavigate();
  const { createToastNotification } = useToastNotificationService();
  const [isSubmit, setIsSubmit] = useState(false);
  const [clientForm, setClientForm] = useReducer(reducer, initState);
  const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false);
  const prepareClientFormState = usePrepareClientFormState(open, brokerId);

  function getEmptyPayload() {
    return getClientPayload(
      {},
      prepareClientFormState.data.brokerClientInfoConfig,
    );
  }

  function getPayload() {
    return getClientPayload(
      {
        ...clientForm,
        [STATE]: clientForm[STATE]?.abbreviation,
        [SITUS_STATE]: clientForm[SITUS_STATE]?.abbreviation,
        [CLIENT_SINCE]: formatDate(clientForm[CLIENT_SINCE]),
      },
      prepareClientFormState.data.brokerClientInfoConfig,
    );
  }

  function handleClose() {
    setClientForm({ type: CLEAN_CLIENT });
    onClose();
  }

  function handleDialogClose() {
    if (isSubmit) return;

    const emptyPayload = getEmptyPayload();
    const initPayload = getPayload();
    if (isEqual(emptyPayload, initPayload)) {
      handleClose();
      return;
    }
    setIsOpenConfirmDialog(true);
  }

  function handleSubmit() {
    if (!clientForm[IS_CLIENT_FORM_VALID]) return;

    const emptyPayload = getEmptyPayload();
    const initPayload = getPayload();
    if (isEqual(emptyPayload, initPayload)) {
      handleClose();
      return;
    }

    setIsSubmit(true);
    const payload = {
      ...initPayload,
      isProspect: false,
      ...(brokerId != null ? { brokerId } : {}),
    };
    api
      .post("/brokerportal/v1/clients", payload)
      .then(({ data }) => {
        navigation(`/${ROUTE_NAMES.CLIENTS}/${data.id}`);
      })
      .catch(() =>
        createToastNotification({
          severity: "error",
          message: "Error creating client. Please try again later!",
        }),
      )
      .finally(() => setIsSubmit(false));
  }

  useEffect(() => {
    if (!prepareClientFormState.data) return;

    const { brokerClientInfoConfig } = prepareClientFormState.data;
    if (brokerClientInfoConfig) {
      setClientForm({
        type: UPDATE_CLIENT_FIELD,
        name: CUSTOM_CONF_FIELDS,
        value: brokerClientInfoConfig,
      });
    }
  }, [prepareClientFormState.data]);

  return (
    <>
      <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>Add Client</DialogTitle>
        <ClientDialogContent
          submitText="ADD CLIENT"
          disabled={!clientForm[IS_CLIENT_FORM_VALID]}
          isSubmit={isSubmit}
          clientForm={clientForm}
          setClientForm={setClientForm}
          prepareClientFormState={prepareClientFormState}
          onSubmit={handleSubmit}
          onCancel={handleDialogClose}
          showClientInformationLabel
        />
      </Dialog>
      <ConfirmDialog
        isOpen={isOpenConfirmDialog}
        title="Confirm cancellation"
        message="Are you sure you want to cancel adding a new Client?"
        confirmBtnMsg="YES, CANCEL"
        closeBtnMsg="NO"
        onClose={() => setIsOpenConfirmDialog(false)}
        onConfirm={() => {
          setIsOpenConfirmDialog(false);
          handleClose();
        }}
      />
    </>
  );
}

export default CreateClientDialog;
